import React, { useEffect, useState } from 'react';
import { Card, Stack, Text, Group, Badge, Timeline, ThemeIcon, Button, Loader, Center } from '@mantine/core';
import { 
  IconActivity, 
  IconRefresh,
  IconClock,
  IconPhoto,
  IconBowl,
  IconMoon,
  IconPlayerPlay,
  IconBook,
  IconBrush,
  IconMusic,
  IconRun
} from '@tabler/icons-react';
import { useTodayActivities } from '../../../hooks/parent/useChildActivities';
import { useStudentActivityUpdates } from '../../../hooks/useRealtimeUpdates';
// import type { ChildActivity } from '../../../services/parentService';

interface LiveActivityFeedProps {
  childId: string;
  maxItems?: number;
  autoRefresh?: boolean;
}

const getActivityIcon = (activityType: string) => {
  const iconMap: Record<string, typeof IconActivity> = {
    'meal': IconBowl,
    'eating': IconBowl,
    'sleep': IconMoon,
    'nap': IconMoon,
    'play': IconPlayerPlay,
    'playing': IconPlayerPlay,
    'learning': IconBook,
    'reading': IconBook,
    'art': IconBrush,
    'music': IconMusic,
    'exercise': IconRun,
    'outdoor': IconRun,
  };
  
  return iconMap[activityType.toLowerCase()] || IconActivity;
};

const getActivityColor = (activityType: string) => {
  const colorMap: Record<string, string> = {
    'meal': 'orange',
    'eating': 'orange',
    'sleep': 'violet',
    'nap': 'violet',
    'play': 'green',
    'playing': 'green',
    'learning': 'blue',
    'reading': 'blue',
    'art': 'pink',
    'music': 'grape',
    'exercise': 'teal',
    'outdoor': 'teal',
  };
  
  return colorMap[activityType.toLowerCase()] || 'gray';
};

export const LiveActivityFeed: React.FC<LiveActivityFeedProps> = ({ 
  childId, 
  maxItems = 10,
  autoRefresh = true 
}) => {
  const { data, isLoading, refetch } = useTodayActivities(childId);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Enable real-time updates
  useStudentActivityUpdates(childId);
  
  const activities = data?.items?.slice(0, maxItems) || [];

  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      refetch();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, refetch]);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit' 
    });
  };

  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMinutes = Math.floor((now.getTime() - activityTime.getTime()) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    if (diffMinutes < 120) return '1 hour ago';
    return `${Math.floor(diffMinutes / 60)} hours ago`;
  };

  if (isLoading && !activities.length) {
    return (
      <Card withBorder>
        <Stack align="center" p="xl">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">Loading activities...</Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="xs">
            <Text size="lg" fw={600}>Live Activity Feed</Text>
            {autoRefresh && (
              <Badge size="xs" color="green" variant="dot">
                Live
              </Badge>
            )}
          </Group>
          <Button
            size="xs"
            variant="subtle"
            leftSection={<IconRefresh size={14} />}
            onClick={handleManualRefresh}
            loading={isRefreshing}
          >
            Refresh
          </Button>
        </Group>

        {activities.length === 0 ? (
          <Center p="xl">
            <Stack align="center" gap="md">
              <ThemeIcon size="xl" radius="xl" variant="light" color="gray">
                <IconClock size={32} />
              </ThemeIcon>
              <div style={{ textAlign: 'center' }}>
                <Text fw={500}>No activities yet</Text>
                <Text size="sm" c="dimmed">
                  Activities will appear here as they happen throughout the day
                </Text>
              </div>
            </Stack>
          </Center>
        ) : (
          <Timeline active={activities.length - 1} bulletSize={36}>
            {activities.map((activity: any) => {
              const Icon = getActivityIcon(activity.activity_type);
              const color = getActivityColor(activity.activity_type);
              
              return (
                <Timeline.Item
                  key={activity.id}
                  bullet={
                    <ThemeIcon size={36} radius="xl" color={color}>
                      <Icon size={20} />
                    </ThemeIcon>
                  }
                  title={
                    <Group justify="space-between">
                      <Text fw={500}>{activity.description}</Text>
                      <Text size="xs" c="dimmed">
                        {getRelativeTime(activity.timestamp)}
                      </Text>
                    </Group>
                  }
                >
                  <Stack gap="xs" mt="xs">
                    <Group gap="xs">
                      <Badge size="sm" variant="light" color={color}>
                        {activity.activity_type}
                      </Badge>
                      {activity.location && (
                        <Badge size="sm" variant="light">
                          {typeof activity.location === 'string' 
                            ? activity.location 
                            : (activity.location as any)?.area || (activity.location as any)?.camera || 'Unknown'}
                        </Badge>
                      )}
                      {activity.verified && (
                        <Badge size="sm" color="green" variant="light">
                          Verified
                        </Badge>
                      )}
                      {activity.image_url && (
                        <Badge 
                          size="sm" 
                          variant="light" 
                          leftSection={<IconPhoto size={12} />}
                        >
                          Photo
                        </Badge>
                      )}
                    </Group>
                    
                    {activity.ai_description && (
                      <Text size="sm" c="dimmed" style={{ fontStyle: 'italic' }}>
                        {activity.ai_description}
                      </Text>
                    )}
                    
                    {activity.teacher_notes && (
                      <Card p="xs" withBorder bg="gray.0">
                        <Text size="xs" fw={500}>Teacher's Note:</Text>
                        <Text size="xs">{activity.teacher_notes}</Text>
                      </Card>
                    )}
                    
                    <Text size="xs" c="dimmed">
                      {formatTime(activity.timestamp)}
                    </Text>
                  </Stack>
                </Timeline.Item>
              );
            })}
          </Timeline>
        )}
      </Stack>
    </Card>
  );
};

export default LiveActivityFeed;